/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { Button, Checkbox, Col, Flex, Form, Input, Row, Select, Table } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { ColumnType } from "antd/es/table";
import { formatNumberFixed, isEditMatchedByStatus, listMatchedStatusTabReview } from "../../../utils/helper";
import { IApWorkflowCommentHistory, IBookingSupplierCostMc, IDataMatchingDetail } from "../../../interfaces/workflow";
import SearchEsp from "./search-esp";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../../../constants/common";
import { PERMISSIONS } from "../../../constants/permission";
import { useHasPermission } from "../../../hooks/useHasPermission";
import SearchSir from "./search-sir";
import { ISirTableData } from "../../../interfaces/invoice";
import { MASTERDATA_TYPE } from "../../../constants/master-data";
import { useMasterData } from "../../../hooks/useMasterData";
import { matchedFlagOptions, MatchedStatusEnum, matchedTeamOptions } from "../../../constants/ap-workflow";

const { TextArea } = Input;

interface MatchingSetFormProps {
  form: any;
  sirList: any[];
  espList: any[];
  onChangeSearchEsp: (data: { expectedSupplierPaymentId: string; bookingSupplierCost: IBookingSupplierCostMc }) => void;
  comments: IApWorkflowCommentHistory[];
  onChangeSearchSir: (invoice: ISirTableData) => void;
  onRemoveInv?: (inv: string) => void;
  matchingDetail: IDataMatchingDetail;
  isTypeFlight: boolean;
}

const MatchingSetForm: React.FC<MatchingSetFormProps> = ({
  form,
  sirList = [],
  espList = [],
  onChangeSearchEsp,
  comments = [],
  onChangeSearchSir,
  onRemoveInv,
  matchingDetail,
  isTypeFlight,
}) => {
  const { isAcceptMultipleInvoices, status, supplierId, isMatchToAlternativeSupplier } = matchingDetail;
  const isViewSearchSir = isAcceptMultipleInvoices || isMatchToAlternativeSupplier;

  const hasEditor = useHasPermission([PERMISSIONS.WORKFLOW_EDITOR]);
  const isDisabledField = !hasEditor || !isEditMatchedByStatus(status);
  const getColumns = (mode: "esp" | "sir"): ColumnType<any>[] => {
    const fieldAmount = mode === "sir" ? "amount" : "localCurrencyCost";
    const fieldOther = isTypeFlight ? "pnrRef" : "leadPax";

    return [
      {
        title: "Supplier Code",
        dataIndex: "supplierCode",
        key: "supplierCode",
        width: 190,
      },
      {
        title: "Booking Reference ID",
        dataIndex: "bookingNo",
        key: "bookingNo",
        width: 190,
      },
      {
        title: isTypeFlight ? "PNR Ref" : "Lead Pax",
        dataIndex: fieldOther,
        key: fieldOther,
        width: 210,
      },
      {
        title: "Amount",
        align: "right",
        dataIndex: fieldAmount,
        key: fieldAmount,
        render: (value) => <div style={{ textAlign: "right" }}>{formatNumberFixed(value)}</div>,
      },
      ...(mode === "sir" && isViewSearchSir && !isDisabledField
        ? [
            {
              title: "",
              dataIndex: "action",
              width: 50,
              render: (_: any, record: any) => {
                const length = sirList.filter((sir: any) => !sir?.invoiceMatchId).length;
                if (!record?.invoiceMatchId && length > 1) {
                  return (
                    <Button
                      size="small"
                      onClick={() => {
                        if (record?.invoiceId) {
                          onRemoveInv?.(record?.invoiceId);
                        }
                      }}
                      type="link"
                      icon={<CloseOutlined />}
                      disabled={isDisabledField}
                    />
                  );
                }
                return null;
              },
            },
          ]
        : []),
    ];
  };
  const sirColumns = useMemo(() => getColumns("sir"), [sirList]);
  const espColumns = useMemo(() => getColumns("esp"), [espList]);
  const sirData = useMemo(
    () =>
      sirList.map((item, index) => ({
        ...item,
        key: index.toString(),
      })),
    [sirList]
  );
  const espData = useMemo(
    () =>
      espList.map((item, index) => ({
        ...item,
        key: index.toString(),
      })),
    [espList]
  );

  const invoiceIds = useMemo(() => {
    const list = sirList.map((item) => item.invoiceId);
    return [...new Set(list)];
  }, [sirList]);

  const types = useMemo(
    () => [
      MASTERDATA_TYPE.NOMINAL_ACCOUNT_CREDIT,
      MASTERDATA_TYPE.NOMINAL_ACCOUNT_DEBIT_All,
      MASTERDATA_TYPE.UNDERS_OVERS_AUTO_MATCH,
    ],
    []
  );

  const { data: masterData } = useMasterData({
    types,
  });

  const { nominalAccountCredits = [],nominalAccountDebitsAll:nominalAccountDebits = [], underOverAutoMatches = [] } = masterData;

  const isNominalCode = useMemo(
    () => [MatchedStatusEnum.EXPORTED, MatchedStatusEnum.COMPLETED].includes(status),
    [status]
  );

  const renderComments = () => (
    <ul className="box-comment">
      {comments.map((item) => (
        <li key={item.id}>
          <div className="text">{item.comment}</div>
          <div className="time">
            {dayjs(item.updatedAt).format(`HH:mm ${DATE_FORMAT}`)} - by {item.userName}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Flex align="center" justify="space-between" gap={16} style={{ margin: "0px 0 10px", whiteSpace: "nowrap" }}>
        <h4 className="sub-title" style={{ margin: 0 }}>
          SIR Matching Set
        </h4>
        {isViewSearchSir && (
          <SearchSir
            onChangeSir={onChangeSearchSir}
            invoiceIds={invoiceIds}
            supplierId={supplierId}
            isDisabled={isDisabledField}
            isTypeFlight={isTypeFlight}
          />
        )}
      </Flex>
      <Table
        dataSource={sirData}
        pagination={false}
        rowKey="key"
        columns={sirColumns}
        size="small"
        className="table-fm"
      />
      <Flex align="center" justify="space-between" gap={16} style={{ margin: "24px 0 10px" }}>
        <h4 className="sub-title" style={{ whiteSpace: "nowrap", margin: 0 }}>
          ESP Matching Set
        </h4>
        <SearchEsp
          onChangeEsp={onChangeSearchEsp}
          supplierId={supplierId}
          sirSupplierId={matchingDetail?.invoices?.[0]?.supplierId}
          isDisabled={isDisabledField || matchingDetail?.pgBookings}
          isTypeFlight={isTypeFlight}
        />
      </Flex>

      <Table
        dataSource={espData}
        pagination={false}
        rowKey="key"
        columns={espColumns}
        size="small"
        className="table-fm"
        style={{ marginBottom: 24 }}
      />
      <Form form={form} layout="vertical" disabled={isDisabledField}>
        <h4 className="sub-title">Actions</h4>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item label="Assign Team" name="team">
              <Select options={matchedTeamOptions} placeholder="Choose Team" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Add Flag" name="flag">
              <Select options={matchedFlagOptions} placeholder="Select Flag" mode="multiple" maxCount={3} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Update Status" name="status">
              <Select
                options={listMatchedStatusTabReview.map((item: string) => ({
                  value: item.toString(),
                  label: item,
                }))}
                placeholder="Choose Status"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={null} name="isLockMatching" valuePropName="checked">
              <Checkbox>Lock Matching</Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <h4 className="sub-title">Nominal Codes</h4>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item
              label="Credit"
              name="credit"
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Select
                options={nominalAccountCredits.map((item: any) => ({
                  value: item.code,
                  label: item.label,
                }))}
                placeholder="Choose Credit"
                disabled={isNominalCode}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Debit"
              name="debit"
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Select
                options={nominalAccountDebits
                  .filter((item: any) => matchingDetail?.pgBookings === item.premiereGolfNominal)
                  .map((item: any) => ({
                    value: item.code,
                    label: item.label,
                  }))}
                placeholder="Choose Debit"
                disabled={isNominalCode}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Unders & Overs"
              name="underOver"
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Select
                options={underOverAutoMatches.map((item: any) => ({
                  value: item.code,
                  label: item.label,
                }))}
                placeholder="Choose Unders & Overs"
                disabled={isNominalCode}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={3}>Comments</Col>
          <Col span={21}>
            <Form.Item label={null} name="comment">
              <TextArea placeholder="Add comment here" autoSize={{ minRows: 2 }} disabled={!hasEditor} />
            </Form.Item>
            {comments.length > 0 && renderComments()}
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default MatchingSetForm;
